import {
  DATE_PIPE_DEFAULT_OPTIONS,
  DatePipeConfig,
  provideImageKitLoader,
  registerLocaleData,
} from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withXsrfConfiguration,
} from '@angular/common/http';
import localeMx from '@angular/common/locales/es-MX';
import {
  APP_ID,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  LOCALE_ID,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideNgIconsConfig } from '@ng-icons/core';
import {
  provideConektaCurrentTheme,
  provideConektaPublicKey,
} from '@seatix/checkout-feat-conekta';
import { provideDigitalfemsaPublicKey } from '@seatix/checkout-feat-digitalfemsa';
import { provideFintocPublicKey } from '@seatix/checkout-feat-fintoc';
import {
  RuntimeAppConfig,
  provideApiUrl,
  provideBreakpoints,
  provideCdnUrl,
  provideEnvironment,
  provideRuntimeConfigService,
  provideStatusUrl,
  provideTurnstileSiteKey,
} from '@seatix/shared-config';
import { provideAlertToast } from '@seatix/shared-util-alert';
import { provideAppVersion } from '@seatix/shared-util-app-version';
import { provideAppVersionRefreshHandler } from '@seatix/shared-util-app-version-refresh-handler';
import { provideSentry } from '@seatix/shared-util-error-tracking';
import { provideLoadingDialog } from '@seatix/shared-util-loading';
import {
  provideThemeInitializer,
  provideThemeTransfer,
  ThemeSwitcherService,
} from '@seatix/shared-util-theme-switcher';
import {
  coreProviders,
  ensureCsrfTokenInterceptor,
  errorInterceptor,
  errorNormalizationInterceptor,
  headersInterceptor,
  provideAppUrls,
} from '@seatix/shared/core';
import {
  provideAppleRedirectUri,
  provideAppleServicesId,
  provideGoogleClientId,
} from '@seatix/shared-social-auth-feature';
import { authInterceptor } from '@seatix/shared/data-access/auth';
import {
  provideVersionInterceptorConfig,
  versionInterceptor,
} from '@seatix/util-http-version-interceptor';
import { appRoutes } from './app.routes';
import { AppId } from '@seatix/shared-domain-app';

registerLocaleData(localeMx, 'es-MX');

export function createAppConfig(
  runtimeConfig: RuntimeAppConfig,
): ApplicationConfig {
  return {
    providers: [
      ...coreProviders,
      provideRouter(
        appRoutes,
        withComponentInputBinding(),
        withInMemoryScrolling({
          scrollPositionRestoration: 'enabled',
        }),
      ),
      provideSentry(),
      provideAppVersionRefreshHandler(),
      provideThemeTransfer(),
      provideThemeInitializer(),
      provideZonelessChangeDetection(),
      provideImageKitLoader(
        `https://ik.imagekit.io/seatix${
          process.env['STX_ENVIRONMENT'] === 'production'
            ? ''
            : `/${process.env['STX_ENVIRONMENT']}`
        }`,
      ),
      provideHttpClient(
        withFetch(),
        withXsrfConfiguration({
          cookieName: 'XSRF-TOKEN',
          headerName: 'X-XSRF-TOKEN',
        }),
        withInterceptors([
          headersInterceptor,
          authInterceptor,
          versionInterceptor,
          ensureCsrfTokenInterceptor,
          errorNormalizationInterceptor,
          errorInterceptor,
        ]),
      ),
      {
        provide: APP_ID,
        useValue: process.env['STX_APP_ID'] as AppId,
      },
      {
        provide: LOCALE_ID,
        useValue: 'es-MX',
      },
      {
        provide: DEFAULT_CURRENCY_CODE,
        useValue: 'MXN',
      },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: {
          appearance: 'outline',
        },
      },
      {
        provide: DATE_PIPE_DEFAULT_OPTIONS,
        useFactory: (): DatePipeConfig => ({
          dateFormat: "EEEE, dd 'de' MMMM 'de' yyyy, hh:mm a",
        }),
      },
      provideBreakpoints(),
      provideNgIconsConfig({
        size: '24px',
      }),
      provideAlertToast(),
      provideLoadingDialog(),
      provideAppUrls(
        runtimeConfig.fanPortalUrl,
        runtimeConfig.controlHubUrl,
        runtimeConfig.controlHubDesktopUrl,
        runtimeConfig.checkoutUrl,
      ),
      provideAppVersion(process.env['STX_APP_VERSION']!),
      provideVersionInterceptorConfig({
        headerName: 'X-Seatix-Checkout-Version',
      }),
      provideApiUrl(process.env['STX_API_URL']!),
      provideCdnUrl(process.env['STX_CDN_URL']!),
      provideStatusUrl(runtimeConfig.statusUrl),
      provideEnvironment(
        process.env['STX_ENVIRONMENT'] as
          | 'development'
          | 'staging'
          | 'production',
      ),
      provideConektaPublicKey(runtimeConfig.conektaPublicKey),
      provideFintocPublicKey(runtimeConfig.fintocPublicKey),
      provideDigitalfemsaPublicKey(runtimeConfig.digitalfemsaPublicKey),
      provideConektaCurrentTheme(() => {
        return inject(ThemeSwitcherService).activeTheme();
      }),
      provideGoogleClientId(runtimeConfig.googleClientId),
      provideAppleServicesId(runtimeConfig.appleServicesId),
      provideAppleRedirectUri(runtimeConfig.appleRedirectUri),
      provideTurnstileSiteKey(runtimeConfig.cloudflareCaptchaSiteKey),
      provideRuntimeConfigService(process.env['STX_CDN_URL']!, runtimeConfig),
    ],
  };
}
